(() => {
  'use strict';

  const SCRIPT_FLAG = '__spintzConsentScriptLoaded';
  const ROOT_SELECTOR = '[data-spintz-consent-root]';
  const CONSENT_EVENT = 'spintz_consent_update';
  const STORAGE_KEY = 'spintz_consent_v3';
  const COOKIE_NAME = 'spintz_consent_v3';
  const STORAGE_VERSION = 3;
  const STORAGE_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;
  const COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;
  const SESSION_SYNC_KEY = 'spintz_consent_synced_v3';
  const DEFAULT_STATE = Object.freeze({
    preferences: false,
    analytics: false,
    marketing: false
  });

  // The section can be re-rendered in Shopify's theme editor. Keep one set of
  // document-level listeners and initialise newly rendered roots separately.
  if (window[SCRIPT_FLAG]) {
    document.dispatchEvent(new CustomEvent('spintz:consent:init'));
    return;
  }
  window[SCRIPT_FLAG] = true;

  const normaliseState = (value) => {
    if (!value || typeof value !== 'object') return null;

    const state = {
      preferences: value.preferences === true,
      analytics: value.analytics === true,
      marketing: value.marketing === true
    };

    const hasBooleans = ['preferences', 'analytics', 'marketing']
      .every((key) => typeof value[key] === 'boolean');

    return hasBooleans ? state : null;
  };

  const normaliseRecord = (value) => {
    if (!value || typeof value !== 'object') return null;
    if (Number(value.version) !== STORAGE_VERSION) return null;

    const state = normaliseState(value);
    if (!state) return null;

    const updatedAt = Number(value.updatedAt || 0);
    if (updatedAt > 0 && Date.now() - updatedAt > STORAGE_MAX_AGE_MS) return null;

    return state;
  };

  const readCookieValue = (name) => {
    const prefix = `${name}=`;
    const entry = document.cookie
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.indexOf(prefix) === 0);

    return entry ? entry.slice(prefix.length) : '';
  };

  const readStoredChoice = () => {
    const bootState = normaliseState(window.__spintzStoredConsent);
    if (bootState) return bootState;

    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const stored = normaliseRecord(JSON.parse(raw));
        if (stored) return stored;
      }
    } catch (error) {
      // Local storage can be unavailable in hardened browser modes.
    }

    try {
      const rawCookie = readCookieValue(COOKIE_NAME);
      if (rawCookie) {
        const stored = normaliseRecord(JSON.parse(decodeURIComponent(rawCookie)));
        if (stored) return stored;
      }
    } catch (error) {
      // Invalid or unavailable cookie: continue as a first visit.
    }

    return null;
  };

  const cookieDomainAttribute = () => {
    const hostname = String(window.location.hostname || '').toLowerCase();
    if (hostname === 'spintz.de' || hostname.endsWith('.spintz.de')) {
      return '; Domain=.spintz.de';
    }
    return '';
  };

  const storeChoice = (state) => {
    const normalised = normaliseState(state);
    if (!normalised) return;

    const record = {
      version: STORAGE_VERSION,
      preferences: normalised.preferences,
      analytics: normalised.analytics,
      marketing: normalised.marketing,
      updatedAt: Date.now()
    };

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch (error) {
      // Cookie fallback below keeps the choice available across pages.
    }

    try {
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(record))}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}${cookieDomainAttribute()}`;
    } catch (error) {
      // Shopify's privacy API remains the primary consent store.
    }

    window.__spintzStoredConsent = { ...normalised };
  };

  const boolFromConsent = (value, fallback) => {
    if (value === 'yes') return true;
    if (value === 'no') return false;
    return Boolean(fallback);
  };

  const loadPrivacyApi = () => new Promise((resolve, reject) => {
    if (window.Shopify && window.Shopify.customerPrivacy) {
      resolve(window.Shopify.customerPrivacy);
      return;
    }

    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
      reject(new Error('Shopify Customer Privacy API loader is unavailable.'));
      return;
    }

    let settled = false;
    const timeout = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error('Shopify Customer Privacy API loading timed out.'));
    }, 8000);

    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      (error) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);

        if (error) {
          reject(error);
          return;
        }

        if (!window.Shopify.customerPrivacy) {
          reject(new Error('Shopify Customer Privacy API did not become available.'));
          return;
        }

        resolve(window.Shopify.customerPrivacy);
      }
    );
  });

  const saveTrackingConsent = (api, state) => new Promise((resolve, reject) => {
    if (!api || typeof api.setTrackingConsent !== 'function') {
      reject(new Error('Shopify setTrackingConsent is unavailable.'));
      return;
    }

    let settled = false;
    const timeout = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error('Shopify consent saving timed out.'));
    }, 10000);

    try {
      api.setTrackingConsent(
        {
          preferences: Boolean(state.preferences),
          analytics: Boolean(state.analytics),
          marketing: Boolean(state.marketing)
        },
        () => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timeout);
          resolve();
        }
      );
    } catch (error) {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      reject(error);
    }
  });

  const readAllowedState = (api) => ({
    preferences: typeof api.preferencesProcessingAllowed === 'function'
      ? api.preferencesProcessingAllowed()
      : false,
    analytics: typeof api.analyticsProcessingAllowed === 'function'
      ? api.analyticsProcessingAllowed()
      : false,
    marketing: typeof api.marketingAllowed === 'function'
      ? api.marketingAllowed()
      : false
  });

  const readVisitorState = (api) => {
    if (!api) {
      return {
        ...DEFAULT_STATE,
        current: {},
        allowed: { ...DEFAULT_STATE }
      };
    }

    const allowed = readAllowedState(api);
    const current = typeof api.currentVisitorConsent === 'function'
      ? api.currentVisitorConsent()
      : {};

    return {
      preferences: boolFromConsent(current.preferences, allowed.preferences),
      analytics: boolFromConsent(current.analytics, allowed.analytics),
      marketing: boolFromConsent(current.marketing, allowed.marketing),
      current,
      allowed
    };
  };

  const hasExplicitChoice = (current = {}) => (
    ['yes', 'no'].includes(current.preferences)
    || ['yes', 'no'].includes(current.analytics)
    || ['yes', 'no'].includes(current.marketing)
  );

  const stateMatchesConsent = (current = {}, state) => (
    current.preferences === (state.preferences ? 'yes' : 'no')
    && current.analytics === (state.analytics ? 'yes' : 'no')
    && current.marketing === (state.marketing ? 'yes' : 'no')
  );

  const sleep = (milliseconds) => new Promise((resolve) => {
    window.setTimeout(resolve, milliseconds);
  });

  const verifyConsent = async (api, state) => {
    if (!api || typeof api.currentVisitorConsent !== 'function') return true;

    for (let attempt = 0; attempt < 8; attempt += 1) {
      const current = api.currentVisitorConsent();
      if (stateMatchesConsent(current, state)) return true;
      await sleep(125);
    }

    return false;
  };

  const updateGoogleConsent = (state, source = 'shopify_customer_privacy') => {
    const consentPayload = {
      analytics_storage: state.analytics ? 'granted' : 'denied',
      ad_storage: state.marketing ? 'granted' : 'denied',
      ad_user_data: state.marketing ? 'granted' : 'denied',
      ad_personalization: state.marketing ? 'granted' : 'denied',
      personalization_storage: state.preferences ? 'granted' : 'denied',
      functionality_storage: state.preferences ? 'granted' : 'denied',
      security_storage: 'granted'
    };

    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
    window.gtag('consent', 'update', consentPayload);

    window.dataLayer.push({
      event: CONSENT_EVENT,
      consent_source: source,
      consent_preferences: state.preferences ? 'granted' : 'denied',
      consent_analytics: state.analytics ? 'granted' : 'denied',
      consent_marketing: state.marketing ? 'granted' : 'denied',
      ...consentPayload
    });

    document.dispatchEvent(new CustomEvent('spintz:consent:changed', {
      detail: { ...state, ...consentPayload, source }
    }));
  };

  const initRoot = (root) => {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const banner = root.querySelector('[data-consent-banner]');
    const backdrop = root.querySelector('[data-consent-backdrop]');
    const modalWrap = root.querySelector('[data-consent-modal-wrap]');
    const modal = root.querySelector('[data-consent-modal]');
    const reopen = root.querySelector('[data-consent-reopen]');
    const status = root.querySelector('[data-consent-status]');
    const preferencesInput = root.querySelector('[data-consent-preferences]');
    const analyticsInput = root.querySelector('[data-consent-analytics]');
    const marketingInput = root.querySelector('[data-consent-marketing]');
    const showMode = root.dataset.showMode || 'shopify';
    const showReopen = false;
    const designMode = root.dataset.designMode === 'true'
      || Boolean(window.Shopify && window.Shopify.designMode);
    const delay = Number.parseInt(root.dataset.delay || '0', 10) || 0;

    let api = null;
    let apiPromise = null;
    let currentState = readStoredChoice() || { ...DEFAULT_STATE };
    let returnFocus = null;
    let bannerRequired = false;
    let saveInProgress = false;

    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    const modalFocusables = () => {
      if (!modal) return [];
      return Array.from(modal.querySelectorAll(focusableSelector)).filter((element) => (
        !element.hidden
        && element.getAttribute('aria-hidden') !== 'true'
        && element.getClientRects().length > 0
      ));
    };

    const trapModalFocus = (event) => {
      if (event.key !== 'Tab' || !modalWrap || modalWrap.hidden) return;
      const focusable = modalFocusables();
      if (!focusable.length) {
        event.preventDefault();
        modal && modal.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === modal)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const ensureApi = () => {
      if (api) return Promise.resolve(api);
      if (!apiPromise) {
        apiPromise = loadPrivacyApi()
          .then((loadedApi) => {
            api = loadedApi;
            return api;
          })
          .catch((error) => {
            apiPromise = null;
            throw error;
          });
      }
      return apiPromise;
    };

    const announce = (message) => {
      if (!status) return;
      status.textContent = '';
      window.setTimeout(() => { status.textContent = message; }, 20);
    };

    const showBanner = () => {
      if (!banner) return;
      banner.hidden = false;
      window.requestAnimationFrame(() => banner.classList.add('is-visible'));
      if (reopen) reopen.hidden = true;
    };

    const hideBanner = () => {
      if (!banner) return;
      banner.classList.remove('is-visible');
      window.setTimeout(() => { banner.hidden = true; }, 230);
    };

    const showReopenButton = () => {
      if (reopen && showReopen && !designMode) reopen.hidden = false;
    };

    const hideReopenButton = () => {
      if (reopen) reopen.hidden = true;
    };

    const hidePreferences = () => {
      const wasOpen = Boolean(modalWrap && !modalWrap.hidden);
      if (modalWrap) modalWrap.hidden = true;
      if (backdrop) backdrop.hidden = true;
      document.documentElement.style.removeProperty('overflow');
      if (wasOpen) document.dispatchEvent(new Event('spintz:consent-preferences-closed'));
    };

    const setInputs = (state) => {
      if (preferencesInput) preferencesInput.checked = Boolean(state.preferences);
      if (analyticsInput) analyticsInput.checked = Boolean(state.analytics);
      if (marketingInput) marketingInput.checked = Boolean(state.marketing);
    };

    const setBusy = (busy) => {
      root.querySelectorAll(
        '[data-consent-accept-all], [data-consent-essential], [data-consent-save]'
      ).forEach((button) => {
        button.disabled = Boolean(busy);
        button.setAttribute('aria-busy', busy ? 'true' : 'false');
      });
    };

    const closePreferences = () => {
      hidePreferences();

      if (bannerRequired && !readStoredChoice()) {
        showBanner();
      } else {
        showReopenButton();
      }

      if (returnFocus && typeof returnFocus.focus === 'function') returnFocus.focus();
      returnFocus = null;
    };

    const openPreferences = (trigger) => {
      returnFocus = trigger || document.activeElement;
      const stored = readStoredChoice();
      currentState = stored || (api ? readVisitorState(api) : currentState);
      setInputs(currentState);
      hideBanner();
      hideReopenButton();
      if (backdrop) backdrop.hidden = false;
      if (modalWrap) modalWrap.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      window.requestAnimationFrame(() => {
        const close = modal ? modal.querySelector('[data-consent-close]') : null;
        if (close) close.focus();
      });
    };

    const persistConsent = async (state, message) => {
      if (saveInProgress) return;
      saveInProgress = true;
      setBusy(true);

      const normalised = normaliseState(state) || { ...DEFAULT_STATE };

      currentState = { ...normalised };
      bannerRequired = false;
      hideBanner();
      hidePreferences();
      hideReopenButton();
      if (returnFocus && typeof returnFocus.focus === 'function') {
        const focusTarget = returnFocus;
        returnFocus = null;
        window.requestAnimationFrame(() => focusTarget.isConnected && focusTarget.focus());
      }

      // The theme editor is only a visual preview. Don't store visitor consent
      // or update tracking signals from inside the editor.
      if (designMode) {
        announce(`${message} (Theme-Vorschau)`);
        setBusy(false);
        saveInProgress = false;
        return;
      }

      // Remember the visitor's explicit choice immediately. This first-party,
      // necessary preference prevents the banner from reappearing during page
      // navigation even if Shopify's privacy API is temporarily unavailable.
      storeChoice(normalised);
      updateGoogleConsent(normalised, 'visitor_choice');

      try {
        const privacyApi = await ensureApi();
        await saveTrackingConsent(privacyApi, normalised);

        let verified = await verifyConsent(privacyApi, normalised);
        if (!verified) {
          // One retry remains tied to the original visitor interaction and
          // covers slow cookie writes in mobile browsers.
          await saveTrackingConsent(privacyApi, normalised);
          verified = await verifyConsent(privacyApi, normalised);
        }

        if (verified) {
          try { window.sessionStorage.setItem(SESSION_SYNC_KEY, '1'); } catch (error) {}
        } else {
          console.warn('[SPINTZ Consent] Shopify consent could not be verified, local choice remains active.');
        }

        showReopenButton();
        announce(message);
      } catch (error) {
        // Do not reopen the banner after an explicit visitor choice. The local
        // record remains available and Shopify sync is retried on a later load.
        console.error('[SPINTZ Consent] Shopify consent could not be saved. Local choice remains active.', error);
        announce(message);
      } finally {
        setBusy(false);
        saveInProgress = false;
      }
    };

    // Bind UI handlers before loading Shopify's API.
    root.querySelectorAll('[data-consent-accept-all]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        persistConsent(
          { preferences: true, analytics: true, marketing: true },
          'Alle optionalen Kategorien wurden aktiviert.'
        );
      });
    });

    root.querySelectorAll('[data-consent-essential]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        persistConsent(
          { preferences: false, analytics: false, marketing: false },
          'Es werden nur notwendige Technologien verwendet.'
        );
      });
    });

    root.querySelectorAll('[data-consent-open-settings]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        openPreferences(button);
      });
    });

    root.querySelectorAll('[data-consent-close]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        closePreferences();
      });
    });

    if (backdrop) backdrop.addEventListener('click', closePreferences);
    if (reopen) reopen.addEventListener('click', () => openPreferences(reopen));

    const saveButton = root.querySelector('[data-consent-save]');
    if (saveButton) {
      saveButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        persistConsent(
          {
            preferences: Boolean(preferencesInput && preferencesInput.checked),
            analytics: Boolean(analyticsInput && analyticsInput.checked),
            marketing: Boolean(marketingInput && marketingInput.checked)
          },
          'Deine Cookie-Einstellungen wurden gespeichert.'
        );
      });
    }

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && modalWrap && !modalWrap.hidden) closePreferences();
    });

    if (modal) modal.addEventListener('keydown', trapModalFocus);

    document.addEventListener('focusin', (event) => {
      if (!modalWrap || modalWrap.hidden || !modal || modal.contains(event.target)) return;
      const focusable = modalFocusables();
      (focusable[0] || modal).focus();
    });

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[href="#spintz-cookie-settings"], [data-spintz-cookie-settings]');
      if (!trigger) return;
      event.preventDefault();
      openPreferences(trigger);
    });

    document.addEventListener('visitorConsentCollected', (event) => {
      const detail = event.detail || {};
      currentState = {
        preferences: Boolean(detail.preferencesAllowed),
        analytics: Boolean(detail.analyticsAllowed),
        marketing: Boolean(detail.marketingAllowed)
      };
      storeChoice(currentState);
      updateGoogleConsent(currentState, 'shopify_visitor_consent_event');
      bannerRequired = false;
      hideBanner();
      showReopenButton();
    });

    window.addEventListener('storage', (event) => {
      if (event.key !== STORAGE_KEY || !event.newValue) return;

      let stored = null;
      try { stored = normaliseRecord(JSON.parse(event.newValue)); } catch (error) {}
      if (!stored) return;

      window.__spintzStoredConsent = { ...stored };
      currentState = stored;
      setInputs(stored);
      updateGoogleConsent(stored, 'stored_choice_other_tab');
      bannerRequired = false;
      hideBanner();
    });

    window.addEventListener('pageshow', () => {
      const stored = readStoredChoice();
      if (!stored || designMode) return;
      currentState = stored;
      setInputs(stored);
      updateGoogleConsent(stored, 'stored_choice_pageshow');
      bannerRequired = false;
      hideBanner();
    });

    // In the editor, selecting the section should make it visible again so it
    // remains easy to customise after testing a button interaction.
    document.addEventListener('shopify:section:select', (event) => {
      if (!designMode) return;
      if (!event.detail || event.detail.sectionId !== root.dataset.sectionId) return;
      bannerRequired = true;
      hidePreferences();
      showBanner();
    });

    window.SPINTZConsent = window.SPINTZConsent || {};
    window.SPINTZConsent.openPreferences = () => openPreferences(document.activeElement);
    window.SPINTZConsent.getState = () => ({ ...currentState });

    if (designMode) {
      bannerRequired = true;
      setInputs(currentState);
      window.setTimeout(showBanner, delay);
      return;
    }

    const storedChoice = readStoredChoice();
    if (storedChoice) {
      currentState = storedChoice;
      setInputs(storedChoice);
      updateGoogleConsent(storedChoice, 'stored_choice');
      bannerRequired = false;
      hideBanner();
      showReopenButton();

      // Keep Shopify-managed pixels and checkout in sync. This replays an
      // already recorded visitor choice only when Shopify has no explicit state.
      ensureApi()
        .then(async (privacyApi) => {
          const shopifyState = readVisitorState(privacyApi);

          if (hasExplicitChoice(shopifyState.current)) {
            currentState = {
              preferences: shopifyState.preferences,
              analytics: shopifyState.analytics,
              marketing: shopifyState.marketing
            };
            storeChoice(currentState);
            updateGoogleConsent(currentState, 'initial_shopify_state');
            return;
          }

          let alreadySynced = false;
          try { alreadySynced = window.sessionStorage.getItem(SESSION_SYNC_KEY) === '1'; } catch (error) {}
          if (alreadySynced) return;

          try {
            await saveTrackingConsent(privacyApi, storedChoice);
            if (await verifyConsent(privacyApi, storedChoice)) {
              try { window.sessionStorage.setItem(SESSION_SYNC_KEY, '1'); } catch (error) {}
            }
          } catch (error) {
            console.warn('[SPINTZ Consent] Stored choice could not be re-synchronised with Shopify.', error);
          }
        })
        .catch((error) => {
          console.warn('[SPINTZ Consent] Shopify privacy API unavailable; stored choice remains active.', error);
        });

      return;
    }

    ensureApi()
      .then((privacyApi) => {
        const initialState = readVisitorState(privacyApi);
        currentState = {
          preferences: initialState.preferences,
          analytics: initialState.analytics,
          marketing: initialState.marketing
        };
        setInputs(currentState);
        updateGoogleConsent(currentState, 'initial_shopify_state');

        if (hasExplicitChoice(initialState.current)) {
          storeChoice(currentState);
          bannerRequired = false;
          hideBanner();
          showReopenButton();
          return;
        }

        bannerRequired = showMode === 'always'
          ? true
          : Boolean(
            typeof privacyApi.shouldShowBanner === 'function'
            && privacyApi.shouldShowBanner()
          );

        if (bannerRequired) {
          window.setTimeout(showBanner, delay);
        }
      })
      .catch((error) => {
        console.error('[SPINTZ Consent] Customer Privacy API could not be loaded.', error);
        updateGoogleConsent(DEFAULT_STATE, 'api_error_default');
        bannerRequired = true;
        window.setTimeout(showBanner, delay);
      });
  };

  const initAll = (container = document) => {
    container.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('spintz:consent:init', () => initAll());
})();
